import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ExternalLink, Apple, Camera } from "lucide-react";

const AppCard = ({ app, index, detailed = false }) => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }
  };

  // Render app icon - either image or fallback
  const renderIcon = (size = "w-6 h-6") => {
    if (app.iconImage) {
      return (
        <img 
          src={app.iconImage} 
          alt={`${app.name} icon`} 
          className={`${size} object-contain`}
        />
      );
    }
    return <Camera className={size} />;
  };

  if (detailed) {
    return (
      <motion.div
        {...fadeInUp}
        className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center py-16 md:py-24 ${
          index % 2 === 1 ? "lg:flex-row-reverse" : ""
        }`}
        data-testid={`app-card-detailed-${app.id}`}
      >
        {/* Content */}
        <div className={`space-y-6 ${index % 2 === 1 ? "lg:order-2" : ""}`}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-sm border border-zinc-100">
              {app.iconImage ? (
                <img src={app.iconImage} alt={`${app.name} icon`} className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-8 h-8 text-zinc-400" />
              )}
            </div>
            <div>
              <h2 className="heading-3">{app.name}</h2>
              <p className="text-sm text-zinc-400">{app.tagline}</p>
            </div>
          </div>
          
          <p className="body-text">{app.description}</p>
          
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-zinc-900">Key Features</h4>
            <ul className="space-y-2">
              {app.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-4">
            <p className="caption mb-3">Ideal for</p>
            <p className="text-sm text-zinc-600">{app.idealFor}</p>
          </div>

          <div className="pt-4 flex flex-wrap gap-4">
            {app.appStoreUrl && (
              <a
                href={app.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
                data-testid={`app-store-link-${app.id}`}
              >
                <Apple className="w-4 h-4" />
                Download on App Store
              </a>
            )}
            {app.privacyUrl && (
              <Link
                to={app.privacyUrl}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid={`privacy-link-${app.id}`}
              >
                Privacy Policy
              </Link>
            )}
          </div>
        </div>

      </motion.div>
    );
  }

  return (
    <motion.div
      {...fadeInUp}
      className="app-card card-hover cursor-pointer group"
      data-testid={`app-card-${app.id}`}
    >
      <div className="p-8 md:p-10 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white flex items-center justify-center mb-6 shadow-sm border border-zinc-100">
          {app.iconImage ? (
            <img src={app.iconImage} alt={`${app.name} icon`} className="w-full h-full object-cover" />
          ) : (
            <Camera className="w-8 h-8 text-zinc-400" />
          )}
        </div>
        <h3 className="text-xl font-semibold text-zinc-900 mb-2">{app.name}</h3>
        <p className="text-sm text-zinc-500 mb-4">{app.tagline}</p>
        <p className="text-sm text-zinc-400 line-clamp-3">{app.shortDescription}</p>
        
        <div className="mt-6 flex items-center gap-2 text-sm font-medium text-zinc-600 group-hover:text-zinc-900 transition-colors">
          Learn more
          <ExternalLink className="w-4 h-4" />
        </div>
      </div>
    </motion.div>
  );
};

export default AppCard;
